import React from "react";

export interface AlertProps {
  variant: "error" | "success" | "warning" | "info";
  children: React.ReactNode;
  dismissible?: boolean;
  onDismiss?: () => void;
  className?: string;
}

/**
 * Alert -- Editorial Grid
 *
 * Inline alert / toast message with semantic color coding.
 * Optional dismiss button.
 */
export const Alert: React.FC<AlertProps> = ({
  variant,
  children,
  dismissible = false,
  onDismiss,
  className = "",
}) => {
  // 123kjkdhjth: background/border were bg-[rgba(...)]/border-[rgba(...)]
  // arbitrary Tailwind values \u2014 literal copies of design.css's
  // --eg-*-bg/--eg-* tokens (drifted slightly: 0.07/0.08 alpha mismatches
  // crept in) baked in as one-off numbers instead of referencing the
  // tokens themselves. Now inline style using the CSS custom properties
  // directly, so a token retune here doesn't require finding every
  // literal copy.
  const variants: Record<
    string,
    { bg: string; border: string; text: string; icon: string }
  > = {
    error: {
      bg: "var(--eg-danger-bg)",
      border: "var(--eg-danger)",
      text: "text-eg-danger",
      icon: "\u2715",
    },
    success: {
      bg: "var(--eg-success-bg)",
      border: "var(--eg-success)",
      text: "text-eg-success",
      icon: "\u2713",
    },
    warning: {
      bg: "var(--eg-amber-bg)",
      border: "var(--eg-amber)",
      text: "text-eg-amber",
      icon: "\u26A0",
    },
    info: {
      bg: "var(--eg-info-bg)",
      border: "var(--eg-info)",
      text: "text-eg-primary",
      icon: "\u2139",
    },
  };

  const v = variants[variant];

  return (
    <div
      role="alert"
      className={[
        "flex items-start gap-3 px-4 py-3",
        "border rounded-eg-md",
        className,
      ].join(" ")}
      style={{ background: v.bg, borderColor: v.border }}
    >
      <span className={`${v.text} flex-shrink-0 mt-0.5 text-eg-md font-bold`}>
        {v.icon}
      </span>
      <div className={`flex-1 font-body text-eg-base ${v.text}`}>
        {children}
      </div>
      {dismissible && onDismiss && (
        <button
          onClick={onDismiss}
          className="flex-shrink-0 text-eg-text-muted hover:text-eg-text transition-colors cursor-pointer bg-transparent border-none text-eg-lg"
          aria-label="Dismiss"
        >
          &times;
        </button>
      )}
    </div>
  );
};
